<template>
    <div :class="['search-is-loading', { 'is-active': restoring }]"></div>
    <div>
        <ol v-if="allHits.length" class="ais-Hits-list">
            <li v-for="hit in allHits" :key="hit.objectID">
                <slot :item="hit" :state="state" />
            </li>
        </ol>
        <div ref="viewMoreTrigger" v-observe-visibility="visibilityChanged"></div>
    </div>
</template>

<script>
    import { createWidgetMixin } from 'vue-instantsearch/vue3/es';
    import { connectInfiniteHits } from 'instantsearch.js/es/connectors';
    import { useIntersectionObserver } from '@vueuse/core';
    import { useSessionStore } from "@/store/storeSession.js";

    const sessionStore = useSessionStore();

    export default {
        mixins: [createWidgetMixin({ connector: connectInfiniteHits })],
        data() {
            return {
                targetVisible: false,
                allHits: [],
                restoring: false,
                restoreTargetHits: 0,
                restoreScrollY: 0,
            }
        },
        props: {
            query: String,
            filters: [String, Object]
        },
        computed: {
            urlMatch() {
                return sessionStore.searchState.url === window.location.href;
            },
            filtersMatch() {
                return String(sessionStore.searchState.filters || '') === String(this.filters || '');
            },
            queryMatch() {
                return String(sessionStore.searchState.query || '') === String(this.query || '');
            },
        },
        watch: {
            'state.query': function(newQuery, oldQuery) {
                if (newQuery !== oldQuery) {
                    this.allHits = []; // Clear previous results
                }
            },
            'state.filters': function(newFilters, oldFilters) {
                if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
                    this.allHits = []; // Clear previous results
                }
            },
            allHits(newHits) {
                this.$emit('update:item-count', newHits.length);

                // If restoring, scroll when we've loaded enough hits or hit last page
                if (this.urlMatch && this.filtersMatch && this.queryMatch && this.restoring && 
                    (newHits.length >= this.restoreTargetHits || (this.state && this.state.isLastPage))) 
                {
                    this.$nextTick(() => {
                        // Wait a tick for DOM, then scroll
                        setTimeout(() => {
                            window.scrollTo(0, this.restoreScrollY || 0);
                            this.restoring = false;
                            sessionStore.searchState.hits = [];
                        }, 50);
                    });
                }
            },
            'state.items': {
                handler(newItems) {
                    if (!Array.isArray(newItems)) return;

                    if (!this.restoring &&
                        (String(sessionStore.searchState.query || '') !== String(this.query || '') ||
                        String(sessionStore.searchState.filters || '') !== String(this.filters || ''))
                    ) {
                        this.allHits = [];
                    }

                    let added = false;
                    newItems.forEach(hit => {
                        if (!this.allHits.find(h => h.objectID === hit.objectID)) {
                            this.allHits.push(hit);
                            added = true;
                        }
                    });
                    if (added) {
                        // Replace the array reference to trigger reactivity
                        this.allHits = this.allHits.slice();
                    }

                    this.$emit('update:item-count', this.allHits.length);
                },
                immediate: true,
            }
        },
        async mounted() {
            await sessionStore.getSearchState();

            useIntersectionObserver(this.$refs.viewMoreTrigger, ([entry]) => {
                this.targetVisible = entry.isIntersecting;
                this.visibilityChanged(this.targetVisible);
            }, { threshold: 0.5 });

            const savedHitCount = sessionStore.searchState.hitCount || 0;

            if (savedHitCount > 0) {
                this.restoring = true;
                this.restoreTargetHits = savedHitCount;
                this.restoreScrollY = sessionStore.searchState.scrollY || 0;

                // Wait for state to be ready
                while (!this.state) {
                    await new Promise(r => setTimeout(r, 50));
                }

                if(this.urlMatch && this.filtersMatch && this.queryMatch) {
                    // Restore hits page by page
                    while (this.allHits.length < this.restoreTargetHits && !this.state.isLastPage) {
                        const prevCount = this.allHits.length;
                        await this.state.showMore();

                        // Wait for allHits to increase or for isLastPage
                        let tries = 0;
                        while (this.allHits.length === prevCount && !this.state.isLastPage && tries < 20) {
                            await new Promise(r => setTimeout(r, 50));
                            tries++;
                        }
                    }

                    // Wait for DOM and images to render
                    await this.$nextTick();
                    await new Promise(r => setTimeout(r, 200));
                    
                    window.scrollTo(0, this.restoreScrollY || 0);
                    this.restoring = false;
                    sessionStore.searchState.hitCount = 0;
                } else {
                    this.restoring = false;
                    sessionStore.setSearchState();
                }
            }
        },
        methods: {
            visibilityChanged(isVisible) {
                // Block infinite scroll during restoration
                if (isVisible && this.state != null && !this.state.isLastPage && !this.restoring) {
                    this.state.showMore();
                }
            },
        },
    };
</script>